import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckSquare, Plus, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";

const PRIOS = { basse: "Basse", normale: "Normale", haute: "Haute" };
const STATUS = { a_faire: "À faire", en_cours: "En cours", termine: "Terminé" };
const empty = { title: "", description: "", due_date: "", priority: "normale", status: "a_faire", client_id: "" };

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [filter, setFilter] = useState("all");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setTasks((await api.get("/tasks")).data);
      setClients((await api.get("/clients")).data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditId(null); setForm(empty); setOpen(true); };
  const openEdit = (t) => { setEditId(t.id); setForm({ ...empty, ...t, client_id: t.client_id || "" }); setOpen(true); };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Titre requis"); return; }
    setSaving(true);
    try {
      const payload = { ...form, client_id: form.client_id || null, due_date: form.due_date || null };
      if (editId) await api.put(`/tasks/${editId}`, payload);
      else await api.post("/tasks", payload);
      toast.success("Tâche enregistrée"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const toggle = async (t) => {
    const next = t.status === "termine" ? "a_faire" : "termine";
    await api.put(`/tasks/${t.id}`, { ...t, status: next, client_id: t.client_id || null });
    load();
  };
  const del = async (id) => { await api.delete(`/tasks/${id}`); toast.success("Supprimée"); load(); };

  const shown = tasks.filter((t) => filter === "all" || t.status === filter);

  return (
    <div>
      <PageHeader title="Tâches" subtitle="Votre centre de tâches — reliez-les à un client, un RDV, un devis…" icon={CheckSquare}
        actions={<Button onClick={openNew} data-testid="btn-new-task" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold"><Plus className="w-4 h-4 mr-1.5" /> Nouvelle tâche</Button>} />

      <div className="flex gap-2 mb-4">
        {["all", "a_faire", "en_cours", "termine"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`task-filter-${f}`}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium ${filter === f ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>
            {f === "all" ? "Toutes" : STATUS[f]}
          </button>
        ))}
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>
        : shown.length === 0 ? <div className="bg-white border border-slate-200 rounded-xl p-12 text-center text-slate-500">Aucune tâche.</div>
        : <div className="space-y-2">{shown.map((t) => (
          <div key={t.id} data-testid={`task-row-${t.id}`} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3">
            <input type="checkbox" checked={t.status === "termine"} onChange={() => toggle(t)} data-testid={`task-check-${t.id}`} className="w-4 h-4 accent-emerald-500" />
            <div className="flex-1 min-w-0 cursor-pointer" onClick={() => openEdit(t)}>
              <div className={`text-sm font-medium ${t.status === "termine" ? "line-through text-slate-400" : "text-slate-800"}`}>{t.title}</div>
              <div className="text-xs text-slate-400 flex gap-2">{t.due_date && <span>Échéance {t.due_date}</span>}<span className="capitalize">{STATUS[t.status]}</span></div>
            </div>
            <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded ${t.priority === "haute" ? "bg-red-100 text-red-600" : t.priority === "basse" ? "bg-slate-100 text-slate-500" : "bg-amber-100 text-amber-600"}`}>{PRIOS[t.priority]}</span>
            <button onClick={() => del(t.id)} data-testid={`task-del-${t.id}`} className="p-2 rounded-lg hover:bg-red-500/10 text-red-600"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}</div>}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-md max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-slate-900">{editId ? "Modifier la tâche" : "Nouvelle tâche"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label className="text-xs text-slate-500">Titre</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-title" className="bg-slate-50 border-slate-200" /></div>
            <div className="space-y-1.5"><Label className="text-xs text-slate-500">Description</Label><Textarea value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-slate-50 border-slate-200" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs text-slate-500">Échéance</Label><Input type="date" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="task-due" className="bg-slate-50 border-slate-200" /></div>
              <div className="space-y-1.5"><Label className="text-xs text-slate-500">Priorité</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}><SelectTrigger data-testid="task-priority" className="bg-slate-50 border-slate-200"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">{Object.entries(PRIOS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs text-slate-500">Statut</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}><SelectTrigger data-testid="task-status" className="bg-slate-50 border-slate-200"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">{Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1.5"><Label className="text-xs text-slate-500">Client (option)</Label>
                <Select value={form.client_id || "none"} onValueChange={(v) => setForm({ ...form, client_id: v === "none" ? "" : v })}><SelectTrigger data-testid="task-client" className="bg-slate-50 border-slate-200"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-200"><SelectItem value="none">Aucun</SelectItem>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name} {c.company}</SelectItem>)}</SelectContent></Select></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={save} disabled={saving} data-testid="btn-save-task" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold">{saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
